import {Injectable} from '@angular/core';
import {EStayRowType, IStayPriceRow, IStayPriceRowTravellerPrice} from "../interfaces/stay-price-row";
import {
    IGetBookingsStayResponse, IGetBookingsVehicleRentalResponse,
    IPrice,
    IPriceDetail,
    IPriceDetailDetail,
    ITraveller,
    IXId,
    XSellLocationEnum
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import {Yii} from "libraries/interfaces/Yii";
import {ITotal} from 'libraries/interfaces/ITotal';
import {
    EVehicleRowType,
    IVehicleRentalPriceRow
} from "../../vehicle-rental-price-tab/interfaces/vehicle-rental-price-intefaces";


@Injectable()
export class RowsMappingService {

    public generateTotalRows(booking: Partial<IGetBookingsStayResponse>): IStayPriceRow[] {
        const totalTotalRow: IStayPriceRow = this.generateTotalRow(
            this.extractTotalPriceWithMerchant(booking.price[0]),
            booking.traveller,
            booking.price[0].detail ? booking.price[0].detail.base : [],
            booking.price[0].detail && booking.price[0].detail.supplement ?
                booking.price[0].detail.supplement.filter((supplement: IPriceDetail) => supplement.xSellLocation === XSellLocationEnum.merchant) :
                [],
            Yii.t('wui-shared-js', 'Dû à la réservation')
        );

        const rows: IStayPriceRow[] = [totalTotalRow];
        const localSupplement: IPriceDetail[] = booking.price[0].detail.supplement ?
            booking.price[0].detail.supplement.filter((supplement: IPriceDetail) => supplement.xSellLocation === XSellLocationEnum.local) :
            [];

        if (localSupplement.length > 0) {
            const totalAddonRow: IStayPriceRow = this.generateTotalRow(
                this.extractTotalPriceWithLocal(booking.price[0]),
                booking.traveller,
                [],
                localSupplement,
                Yii.t('wui-shared-js', 'À payer sur place'),
            );
            rows.push(totalAddonRow);
        }
        return rows.filter((item: IStayPriceRow) => item);
    }


    public generateDetailRows(booking: Partial<IGetBookingsStayResponse>): IStayPriceRow[] {
        const totalTotalRow: IStayPriceRow = this.generateCollapserRow(
            this.extractTotalPriceWithMerchant(booking.price[0]),
            booking.traveller,
            booking.price[0].detail ? booking.price[0].detail.base : [],
            booking.price[0].detail && booking.price[0].detail.supplement ?
                booking.price[0].detail.supplement.filter((supplement: IPriceDetail) => supplement.xSellLocation === XSellLocationEnum.merchant) :
                [], Yii.t('wui-shared-js', 'Dû à la réservation'),
            'merchant'
        );

        const rows: IStayPriceRow[] = [
            totalTotalRow,
            ...this.generateGroupRow(
                this.extractDetailRowMerchant(
                    booking.price[0].detail ? booking.price[0].detail.base : null,
                    booking.price[0].detail ? booking.price[0].detail.supplement : null, booking.traveller),
                'merchant'
            )
        ];

        const localSupplement: IPriceDetail[] = booking.price[0].detail.supplement ?
            booking.price[0].detail.supplement.filter((supplement: IPriceDetail) => supplement.xSellLocation === XSellLocationEnum.local) :
            [];

        if (localSupplement.length > 0) {
            const totalAddonRow: IStayPriceRow = this.generateCollapserRow(
                this.extractTotalPriceWithLocal(booking.price[0]),
                booking.traveller,
                [],
                localSupplement,
                Yii.t('wui-shared-js', 'À payer sur place'),
                'local'
            );

            rows.push(totalAddonRow,
                ...this.generateGroupRow(
                    this.extractDetailRowLocal(booking.price[0].detail ? booking.price[0].detail.supplement : null),
                    'local'
                )
            );
        }

        return rows.filter((item: IStayPriceRow) => item);
    }

    private generateTotalRow(
        price: ITotal,
        travellers: ITraveller[],
        bases: IPriceDetail[],
        supplements: IPriceDetail[],
        title: string = ""
    ): IStayPriceRow {
        if (!price) {
            return undefined;
        }
        return {...this.generateRow(price, travellers, title, bases, supplements), isHeaderRow: true};
    }

    private generateCollapserRow(
        price: ITotal,
        travellers: ITraveller[],
        bases: IPriceDetail[],
        supplements: IPriceDetail[],
        title: string = "",
        collapser: string
    ): IStayPriceRow {
        if (!price) {
            return undefined;
        }
        return {...this.generateRow(price, travellers, title, bases, supplements), collapser};
    }

    private generateGroupRow(priceRows: IStayPriceRow[], collapser: string): IStayPriceRow[] {
        return priceRows.map((priceRow: IStayPriceRow) => ({...priceRow, collapseGroup: collapser}));
    }

    private generateRow(
        price: ITotal,
        travellers: ITraveller[],
        title: string = "",
        bases: IPriceDetail[],
        supplements: IPriceDetail[],
        undefinedValue: number = 0
    ): IStayPriceRow {
        if (price) {
            const amount: number = Number(price.amount);
            const currency: string = price.currency;

            const traveller: IStayPriceRowTravellerPrice[] = (travellers || [])
                .map((travellerItem: ITraveller) => {
                    const travellerBasePrice: IPriceDetail = this.findTravellerDetailPrice(travellerItem.xId, bases);

                    const travellerSupplementPrice: IPriceDetail = supplements && supplements.length ?
                        this.findTravellerDetailPrice(travellerItem.xId, supplements) :
                        undefined;

                    const nbTravellerInPrice: number = travellerBasePrice ?
                        travellerBasePrice.traveller.length :
                        travellerSupplementPrice ? travellerSupplementPrice.traveller.length : null;
                    return {
                        id: travellerItem.xId,
                        amount: nbTravellerInPrice ?
                            (
                                (travellerBasePrice ? Number(travellerBasePrice.amount) / nbTravellerInPrice : 0) +
                                (travellerSupplementPrice ? Number(travellerSupplementPrice.amount) / nbTravellerInPrice : 0)
                            ) :
                            undefinedValue,
                        currency,
                    };
                });

            return {
                title,
                total: {
                    amount,
                    currency
                },
                traveller
            };

        }
        return undefined;
    }

    private extractDetailRowLocal(supplements: IPriceDetail[]): IStayPriceRow[] {
        const localDetails: IPriceDetail[] = (supplements || [])
            .filter((supplement: IPriceDetail): boolean => supplement.xSellLocation === XSellLocationEnum.local);

        return localDetails.reduce((rows: IStayPriceRow[], localDetail: IPriceDetail): IStayPriceRow[] => {

            Object.values(localDetail.detail).forEach(
                (detailArg: IPriceDetailDetail): void => {
                    let detail: IPriceDetailDetail;
                    if(!detailArg.amount && detailArg.originalCurrency){
                        // @ts-ignore
                        detail = {
                            ...detailArg,
                            ...detailArg.originalCurrency,
                        };
                    }else{
                        detail = detailArg;
                    }

                    const rowToComplete: IStayPriceRow = rows.find((row: IStayPriceRow): boolean => row.title === detail.name);
                    const nbTravellers: number = localDetail.traveller.length;
                    const travellersToAd: IStayPriceRowTravellerPrice[] = localDetail.traveller
                        .map((traveller: IXId): IStayPriceRowTravellerPrice => {
                            return {
                                id: traveller.xId,
                                currency: detail.currency,
                                amount: Number(detail.amount) / nbTravellers
                            };
                        });

                    if (!rowToComplete) {
                        rows.push({
                            title: detail.name,
                            total: {
                                amount: Number(detail.amount),
                                currency: detail.currency
                            },
                            traveller: travellersToAd
                        });
                    } else {
                        const amountToAdd: number = Number(detail.amount);
                        rowToComplete.total.amount += amountToAdd;
                        rowToComplete.traveller = [...rowToComplete.traveller, ...travellersToAd];
                    }
                }
            );
            return rows;
        }, []);
    }

    private extractDetailRowMerchant(detailBases: IPriceDetail[], supplements: IPriceDetail[], travellers: ITraveller[]): IStayPriceRow[] {

        const amount: number = (detailBases || [])
            .reduce((amountTotal: number, price: IPriceDetail) => amountTotal + Number(price.amount), 0);

        const supplementDetails: IPriceDetail[] = this.extractSupplementPerType(XSellLocationEnum.merchant, supplements);
        const supplementAmount: number = (supplementDetails || [])
            .reduce((amountTotal: number, price: IPriceDetail) => amountTotal + Number(price.amount), 0);

        const detail: IStayPriceRow[] = supplementDetails && supplementDetails.length ? [{
            title: Yii.t('wui-shared-js', 'Taxes et frais'),
            type: EStayRowType.fee,
            total: {
                amount: supplementAmount,
                currency: supplementDetails[0].currency
            },
            traveller: travellers.map((bookingTraveller: ITraveller) => {

                const price: IPriceDetail = this.findTravellerDetailPrice(bookingTraveller.xId, supplementDetails);
                return {
                    amount: price.amount / price.traveller.length,
                    currency: price.currency,
                    id: bookingTraveller.xId,
                };
            })
        }] : [];

        return [
            {
                title: Yii.t('wui-shared-js', 'Prix de base'),
                type: EStayRowType.base,
                total: {
                    amount,
                    currency: detailBases ? detailBases[0].currency : 'EUR',
                },
                traveller: (travellers || [])
                    .map((bookingTraveller: ITraveller) => {
                        const price: IPriceDetail = this.findTravellerDetailPrice(bookingTraveller.xId, detailBases);
                        return {
                            amount: price.amount / price.traveller.length,
                            currency: price.currency,
                            id: bookingTraveller.xId,
                        };
                    })
            },
            ...detail
        ];

    }

    private findTravellerDetailPrice(travellerXId: string, detailPrices: IPriceDetail[]): IPriceDetail {
        return detailPrices
            .find((detailBase: IPriceDetail) => {
                return detailBase.traveller
                    .map(({xId}: IXId) => xId)
                    .includes(travellerXId);
            });
    }

    private extractTotalPriceWithMerchant(bookingPrice: IPrice): ITotal {
        const base: ITotal = bookingPrice.total.base;
        const supplementAmount: number = bookingPrice.detail && bookingPrice.detail.supplement ?
            this.extractSupplementPerType(XSellLocationEnum.merchant, bookingPrice.detail.supplement)
                .reduce((amount: number, supplement: IPriceDetail) => {
                    return amount + Number(supplement.amount);
                }, 0) : 0;

        return {...base, amount: (base ? base.amount : 0) + (supplementAmount ? supplementAmount : 0)};
    }

    private extractTotalPriceWithLocal(bookingPrice: IPrice): ITotal {
        const base: ITotal = bookingPrice.total.base;
        let currency: string = base.currency;
        const supplementAmount: number = bookingPrice.detail && bookingPrice.detail.supplement ?
            this.extractSupplementPerType(XSellLocationEnum.local, bookingPrice.detail.supplement)
                .reduce((amount: number, supplement: IPriceDetail) => {
                    currency = supplement.currency;
                    return amount + Number(supplement.amount);
                }, 0) : 0;

        return bookingPrice.detail && bookingPrice.detail.supplement ?
            {...base, amount: supplementAmount, currency} :
            undefined;
    }

    private extractSupplementPerType(type: XSellLocationEnum, priceDetails: IPriceDetail[]): IPriceDetail[] {
        return (priceDetails || [])
            .filter((supplement: IPriceDetail) => supplement.xSellLocation === type);
    }
}
